import React, { useEffect, useState, useCallback } from 'react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import { GradientButton } from '@/src/modules/components/ui/gradient-button';
import { cn } from '@/src/infrastructure/lib/utils';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ApiInvitation, ApiInvitationStatus } from '@/src/types';
import { getInvitations, deleteInvitation } from '@/src/infrastructure/lib/api';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { useFormat } from '@/src/infrastructure/lib/format';
import { PublishCreditsCard } from '@/src/modules/entitlements/components/PublishCreditsCard';
import { generateShareMessage } from '@/src/infrastructure/lib/share';
import { trackEvent } from '@/src/infrastructure/lib/analytics';

const STATUS_LABEL: Record<ApiInvitationStatus, string> = {
  PUBLISHED: 'Live',
  DRAFT: 'Draft',
  PROCESSING: 'Sending...',
  FAILED: 'Failed',
  EXPIRED: 'Expired',
  ARCHIVED: 'Archived',
  CANCELLED: 'Cancelled',
  SCHEDULED: 'Scheduled',
  SUSPENDED: 'Suspended',
  REPORTED: 'Reported',
};

const STATUS_CLASS: Record<ApiInvitationStatus, string> = {
  PUBLISHED: 'bg-status-published/10 text-status-published',
  DRAFT: 'bg-status-draft/10 text-status-draft',
  PROCESSING: 'bg-status-processing/10 text-status-processing',
  FAILED: 'bg-status-failed/10 text-status-failed',
  EXPIRED: 'bg-on-surface-variant/10 text-on-surface-variant',
  ARCHIVED: 'bg-on-surface-variant/10 text-on-surface-variant',
  CANCELLED: 'bg-status-failed/10 text-status-failed',
  SCHEDULED: 'bg-status-processing/10 text-status-processing',
  SUSPENDED: 'bg-status-failed/10 text-status-failed',
  REPORTED: 'bg-status-failed/10 text-status-failed',
};

interface DashboardCardProps {
  invite: ApiInvitation;
  formatDate: (date: string) => string;
  handleDelete: (id: string) => Promise<void>;
  copyLink: (url: string) => Promise<void>;
  isDeleting: boolean;
}

const DashboardCard = React.memo(function DashboardCard({
  invite,
  formatDate,
  handleDelete,
  copyLink,
  isDeleting,
}: DashboardCardProps) {
  return (
    <motion.div
      layoutId={invite.id}
      whileHover={{ y: -4 }}
      className="bg-surface rounded-[2rem] border border-outline-variant/30 shadow-sm overflow-hidden flex flex-col group hover:shadow-xl transition-all duration-300"
    >
      <div className="h-48 relative bg-surface-low overflow-hidden">
        <div className="w-full h-full bg-gradient-to-br from-surface-low to-outline-variant/10 flex items-center justify-center">
          <Icons.Calendar size={48} className="text-on-surface-variant/20" />
        </div>
        <div className="absolute top-4 right-4">
          <span className={cn(
            'inline-flex items-center px-3 py-1 rounded-full font-bold text-xs shadow-sm',
            STATUS_CLASS[invite.status],
          )}>
            {STATUS_LABEL[invite.status]}
          </span>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-on-surface mb-2 line-clamp-1">{invite.eventName}</h3>
        <p className="text-sm flex items-center gap-2 text-on-surface-variant">
          <Icons.Calendar size={14} className="text-primary" />
          {formatDate(invite.eventDate)}
        </p>

        <div className="mt-auto flex justify-between items-center pt-4 border-t border-outline-variant/10 mt-6">
          <span className="text-xs text-on-surface-variant font-medium">
            {invite.hostName}
          </span>
          <div className="flex items-center gap-1">
            {invite.status === 'PUBLISHED' && invite.hostedUrl && (
              <>
                <a
                  href={invite.hostedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Open invitation"
                >
                  <Icons.ExternalLink size={16} />
                </a>
                <button
                  onClick={() => copyLink(invite.hostedUrl!)}
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Copy link"
                >
                  <Icons.Copy size={16} />
                </button>
                <a
                  href={`mailto:?subject=${encodeURIComponent(invite.eventName)}&body=${encodeURIComponent(invite.hostedUrl)}`}
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Share by email"
                >
                  <Icons.Mail size={16} />
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(generateShareMessage(invite, invite.hostedUrl || ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Share on WhatsApp"
                >
                  <Icons.WhatsApp size={16} />
                </a>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-status-failed font-bold"
              onClick={() => handleDelete(invite.id)}
              disabled={isDeleting}
            >
              {isDeleting ? '...' : 'Delete'}
            </Button>
          </div>
        </div>

        {invite.status === 'PROCESSING' && (
          <div className="mt-4 w-full bg-surface-low rounded-full h-1.5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '65%' }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="bg-status-processing h-1.5 rounded-full"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
});

export function Dashboard() {
  const { user } = useAuth();
  const fmt = useFormat();
  const formatDate = fmt.date;
  const [invitations, setInvitations] = useState<ApiInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getInvitations();
      setInvitations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    trackEvent('DASHBOARD_VIEW', 'navigation');
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this invitation? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteInvitation(id);
      setInvitations(prev => prev.filter(inv => inv.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const copyLink = useCallback(async (url: string) => {
    await navigator.clipboard.writeText(url);
  }, []);

  // Drafts are surfaced via a compact summary widget — not as full cards in the
  // grid. The grid stays focused on published/active events that answer "what is
  // happening now?". Users manage draft detail via Invitations → Drafts tab.
  const draftCount = invitations.filter((inv) => inv.status === 'DRAFT').length;
  const visibleInvitations = invitations.filter((inv) => inv.status !== 'DRAFT');

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-8">
      <DashboardHeader
        title="My Invitations"
        description={`Welcome back${user?.displayName ? ', ' + user.displayName : ''}. Manage and track your events.`}
      />

      <div className="px-8 py-8 max-w-7xl mx-auto w-full">
        <PublishCreditsCard />

        {draftCount > 0 && (() => {
          const mostRecentDraft = invitations.find((inv) => inv.status === 'DRAFT');
          const editHref = mostRecentDraft
            ? `/wizard?draftId=${encodeURIComponent(mostRecentDraft.id)}`
            : '/dashboard/invitations?tab=drafts';
          return (
            <div className="mb-8 flex flex-col gap-3 rounded-3xl border border-outline-variant/30 bg-surface p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-surface-low text-on-surface-variant">
                  <Icons.Other size={20} />
                </span>
                <div>
                  <p className="font-bold text-on-surface">Draft Invitations</p>
                  <p className="text-sm text-on-surface-variant">
                    {draftCount} {draftCount === 1 ? 'draft' : 'drafts'} in progress — your work is saved automatically.
                  </p>
                </div>
              </div>
              <Link to={editHref}>
                <Button size="md" variant="outline">Continue Editing</Button>
              </Link>
            </div>
          );
        })()}

        <div className="flex justify-between items-center mb-8">
          <div />
          <Link to="/wizard">
            <GradientButton className="flex items-center gap-2">
              <Icons.Plus size={18} />
              Create Invitation
            </GradientButton>
          </Link>
        </div>

        {loading && (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16 text-status-failed font-medium">{error}</div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visibleInvitations.map((invite) => (
              <DashboardCard
                key={invite.id}
                invite={invite}
                formatDate={formatDate}
                handleDelete={handleDelete}
                copyLink={copyLink}
                isDeleting={deletingId === invite.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
